import React, { useState } from 'react';
import { Alert, Box, Button, InputAdornment, Link, TextField } from '@mui/material';
import { LockOutlined, MailOutline } from '@mui/icons-material';
import { requestCode, verifyCode } from './licApi';
import { isEmail } from './orgFormat';
import { SubmitLabel, fieldSx, submitSx } from '../../components/AuthLayout';
import { colors } from '../../styles/tokens';

const errorText = (error) => {
  if (error?.code === 'NotEligible') {
    return "This email isn't an IT admin for any organization. Team members sign in on the User tab with their "
      + 'Vavetek account, and get their license in HUBAROC with the org code.';
  }
  if (error?.code === 'InvalidCode') {
    return 'That code is wrong or has expired. Check it, or send a new one.';
  }
  return error?.message || 'Something went wrong. Please try again.';
};

/**
 * IT admin sign-in for the org licensing dashboard: email, then the 6-digit code we email. Rendered on the IT admin
 * side of the shared /login page. Calls onSignedIn once the session is saved.
 */
const OrgAdminSignIn = ({ onSignedIn }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const sendCode = async () => {
    setError('');
    setBusy(true);
    try {
      const result = await requestCode(email.trim());
      setInfo(result?.message || `If this email can sign in, a code has been sent to ${email.trim()}.`);
      setStep('code');
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const handleRequest = (event) => {
    event.preventDefault();
    sendCode();
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyCode(email.trim(), code.trim());
      onSignedIn();
    } catch (verifyError) {
      setError(errorText(verifyError));
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setCode('');
    await sendCode();
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}
      {step === 'code' && info && !error && <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>{info}</Alert>}

      {step === 'email' ? (
        <Box component="form" onSubmit={handleRequest}>
          <TextField
            id="org-lic-email"
            type="email"
            placeholder="Work email"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={fieldSx}
            inputProps={{ 'aria-label': 'Work email' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline sx={{ color: colors.muted }} />
                </InputAdornment>
              ),
            }}
          />
          <Box mt={3}>
            <Button type="submit" variant="contained" size="large" fullWidth disabled={busy || !isEmail(email)} sx={submitSx}>
              <SubmitLabel busy={busy}>Send code</SubmitLabel>
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleVerify}>
          <TextField
            id="org-lic-code"
            placeholder="6-digit code"
            autoComplete="one-time-code"
            autoFocus
            required
            fullWidth
            inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]{6}', 'aria-label': '6-digit code' }}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: colors.muted }} />
                </InputAdornment>
              ),
            }}
          />
          <Box mt={2} display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Link
              component="button"
              type="button"
              variant="body2"
              disabled={busy}
              onClick={() => {
                setStep('email');
                setCode('');
                setError('');
                setInfo('');
              }}
            >
              Use a different email
            </Link>
            <Link component="button" type="button" variant="body2" disabled={busy} onClick={handleResend}>
              Resend code
            </Link>
          </Box>
          <Box mt={3}>
            <Button type="submit" variant="contained" size="large" fullWidth disabled={busy || code.length !== 6} sx={submitSx}>
              <SubmitLabel busy={busy}>Log in</SubmitLabel>
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrgAdminSignIn;
